import React, { useEffect, useMemo, useState } from 'react';
import { Inbox, X } from 'lucide-react';
import SearchSubmitInput from '../common/SearchSubmitInput.js';
import cashbookService from '../../services/CashbookService.js';
import Alert from '../common/Alert';
import Loader from '../common/Loader.js';
import { formatCurrencyPaise } from '../../utils/formatCurrency.js';
import { formatIndianDate } from '../../utils/localDate.js';

const money = (v) => formatCurrencyPaise(v, '₹', { alwaysPaise: true });

const DayBookCard = ({ title, rows, reserve = false }) => {
  const total = rows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0);
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm flex flex-col min-h-[280px]">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
        {reserve ? (
          <span className="text-[10px] uppercase tracking-wide font-semibold text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">
            Reserve
          </span>
        ) : null}
      </div>
      <div className="px-3 pt-2 flex-1 min-h-0">
        <div className="grid grid-cols-[2rem_1fr_4.5rem] gap-2 text-[11px] uppercase text-gray-400 px-1 pb-1">
          <span>S.No</span>
          <span>LR No</span>
          <span className="text-right">Amount</span>
        </div>
        <div className="max-h-52 overflow-y-auto">
          {rows.length === 0 ? (
            <div className="flex flex-col items-center justify-center text-gray-300 py-8">
              <Inbox className="w-8 h-8" />
              <p className="text-xs mt-1 text-gray-400">No data</p>
            </div>
          ) : (
            rows.map((row, index) => (
              <div
                key={`${row.lr_no}-${row.date || ''}-${index}`}
                className="grid grid-cols-[2rem_1fr_4.5rem] gap-2 items-start px-1 py-1.5 text-sm border-t border-gray-50"
              >
                <span className="text-gray-400">{index + 1}</span>
                <span className="min-w-0">
                  <span className="block text-gray-800 font-medium break-all">{row.lr_no}</span>
                  {row.date ? (
                    <span className="block text-[11px] text-gray-400">{formatIndianDate(row.date)}</span>
                  ) : null}
                </span>
                <span className={`text-right whitespace-nowrap font-medium ${reserve ? 'text-amber-700' : 'text-gray-800'}`}>
                  {money(row.amount)}
                </span>
              </div>
            ))
          )}
        </div>
      </div>
      <div className={`mx-3 mb-3 mt-2 px-3 py-2 rounded-lg flex items-center justify-between text-sm ${reserve ? 'bg-amber-50 text-amber-900' : 'bg-blue-50 text-blue-900'}`}>
        <span className="font-medium">Total Amount</span>
        <span className="font-semibold">{money(total)}</span>
      </div>
    </div>
  );
};

const CashbookDayBookModal = ({
  open,
  onClose,
  fromDate,
  toDate,
  locationHubId,
  ownerUserId,
}) => {
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setLoading(true);
    cashbookService.getDayBook(fromDate, toDate, locationHubId, ownerUserId)
      .then((res) => {
        if (active) setBook(res.data?.data || null);
      })
      .catch((err) => {
        if (!active) return;
        setBook(null);
        Alert.error(err.response?.data?.error || 'Failed to load day book');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [open, fromDate, toDate, locationHubId, ownerUserId]);

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const sections = useMemo(() => {
    const raw = book?.sections || [];
    const needle = query.trim().toLowerCase();
    return raw
      .filter((section) => !section.reserve || book?.labour_reserve_enabled)
      .map((section) => ({
        ...section,
        rows: needle
          ? (section.rows || []).filter((row) => String(row.lr_no || '').toLowerCase().includes(needle))
          : (section.rows || []),
      }));
  }, [book, query]);

  const cashTotal = useMemo(
    () => sections.filter((s) => !s.reserve).reduce(
      (sum, section) => sum + section.rows.reduce((inner, row) => inner + (Number(row.amount) || 0), 0),
      0,
    ),
    [sections],
  );
  const reserveTotal = useMemo(
    () => sections.filter((s) => s.reserve).reduce(
      (sum, section) => sum + section.rows.reduce((inner, row) => inner + (Number(row.amount) || 0), 0),
      0,
    ),
    [sections],
  );

  if (!open) return null;

  const rangeLabel = fromDate && toDate && fromDate !== toDate
    ? `${formatIndianDate(fromDate)} – ${formatIndianDate(toDate)}`
    : formatIndianDate(fromDate || toDate);

  return (
    <div className="fixed left-0 right-0 bottom-0 top-16 sm:top-20 z-[40] bg-black/50 flex items-center justify-center p-3 sm:p-4">
      <div className="bg-gray-50 w-full sm:max-w-6xl max-h-full sm:rounded-2xl shadow-xl flex flex-col overflow-hidden">
        <div className="bg-white px-4 py-3 border-b border-gray-200 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-gray-800">Day Book</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {book?.book_label ? `${book.book_label} · ` : ''}
              {rangeLabel}
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-700 p-1" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-4 py-3 bg-white border-b border-gray-100 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex-1 min-w-0">
            <SearchSubmitInput
              value={query}
              onSearch={setQuery}
              placeholder="Search LR No"
              ariaLabel="Search LR No"
              inputClassName="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-400"
            />
          </div>
          <div className="flex flex-wrap gap-2 text-sm">
            <span className="px-3 py-1.5 rounded-lg bg-rose-50 text-rose-800 font-medium">
              Period total {money(cashTotal)}
            </span>
            {book?.labour_reserve_enabled ? (
              <span className="px-3 py-1.5 rounded-lg bg-amber-50 text-amber-800 font-medium">
                Reserve held {money(reserveTotal)}
              </span>
            ) : null}
          </div>
        </div>

        <div className="overflow-y-auto p-4">
          {loading ? (
            <Loader fullScreen={false} message="Loading day book..." />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {sections.map((section) => (
                <DayBookCard
                  key={section.key}
                  title={section.title}
                  rows={section.rows}
                  reserve={!!section.reserve}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CashbookDayBookModal;
